import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { QboModal, QboTypography, TooltipWrapper } from '@ui/Components';
import QboCardSingleList from '@ui/Components/QboCardSingleList';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { prism } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { IconButton } from '@mui/material';
import { ContentCopy } from '@mui/icons-material';

function getOrigin(url) {
  if (!url) {
    return '';
  }

  try {
    return new URL(url).origin;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Invalid URL:', error, url);
    return '';
  }
}

function copyToClipboard(data) {
  navigator.clipboard.writeText(JSON.stringify(data, null, 2));
  alert('Copied to clipboard!');
}

export default function AuditLogDetailsSection({ auditLog, onClose }) {
  const { t } = useTranslation();
  const details = useMemo(() => {
    return auditLog
      ? [
          { id: 1, value: auditLog.id, label: t('audit_logs.labels.log_id') },
          {
            id: 2,
            value: dayjs(auditLog.created_at).format('DD MMM YYYY hh:mm A Z'),
            label: t('audit_logs.labels.date'),
          },
          { id: 3, value: auditLog.actor_email, label: t('audit_logs.labels.actor') },
          { id: 4, value: auditLog.actor_uid, label: t('audit_logs.labels.actor_uid') },
          { id: 5, value: auditLog.actor_role, label: t('audit_logs.labels.actor_role') },
          {
            id: 6,
            value: auditLog.actor_reseller_name,
            label: t('audit_logs.labels.partner_parent'),
          },
          {
            id: 7,
            value: auditLog.actor_reseller_uid,
            label: t('audit_logs.labels.partner_parent_id'),
          },
          { id: 8, value: auditLog.activity, label: t('audit_logs.labels.activity') },
          { id: 9, value: auditLog.source, label: t('audit_logs.labels.source') },
          { id: 10, value: auditLog.description, label: t('audit_logs.labels.description') },
          { id: 11, value: auditLog.object_name, label: t('audit_logs.labels.object') },
          { id: 12, value: auditLog.object_uid, label: t('audit_logs.labels.object_uid') },
          { id: 13, value: auditLog.object_type, label: t('audit_logs.labels.object_type') },
          {
            id: 14,
            value: auditLog.object_reseller_name,
            label: t('audit_logs.labels.object_partner_parent'),
          },
          {
            id: 15,
            value: auditLog.object_reseller_uid,
            label: t('audit_logs.labels.object_partner_parent_id'),
          },
          { id: 16, value: auditLog.status, label: t('audit_logs.labels.status') },
          { id: 17, value: auditLog.user_agent, label: t('audit_logs.labels.browser_agent') },
          {
            id: 18,
            value: getOrigin(auditLog.url_origin),
            label: t('audit_logs.labels.url_origin'),
          },
        ]
      : [];
  }, [auditLog]);

  if (!auditLog) return null;
  return (
    <QboModal
      onClose={onClose}
      open={!!auditLog}
      title="Audit Log Details"
      showCloseIcon
      preventOutsideClick>
      {details.map((detail) => (
        <QboCardSingleList key={detail.id} leftContent={detail.label} rightContent={detail.value} />
      ))}

      {auditLog.response_body && (
        <>
          <div
            style={{
              margin: '2rem 0 1rem 0',
              display: 'flex',
              alignItems: 'center',
              gap: '1.2rem',
            }}>
            <QboTypography style={{ fontSize: '2rem' }}>Response Body</QboTypography>
            <TooltipWrapper condition title="Copy to clipboard">
              <IconButton
                style={{ width: 'max-content' }}
                sx={{
                  position: 'relative',
                  right: '0.7rem',
                }}
                onClick={() => copyToClipboard(auditLog.response_body)}>
                <ContentCopy sx={{ width: '2rem', height: '2rem' }} />
              </IconButton>
            </TooltipWrapper>
          </div>
          <SyntaxHighlighter
            language="javascript"
            style={prism}
            showLineNumbers
            customStyle={{
              borderRadius: '5px',
              fontSize: '14px',
              padding: '10px',
            }}>
            {JSON.stringify(auditLog.response_body, null, 2)}
          </SyntaxHighlighter>
        </>
      )}

      {auditLog.request_body && (
        <>
          <div
            style={{
              margin: '2rem 0 1rem 0',
              display: 'flex',
              alignItems: 'center',
              gap: '1.2rem',
            }}>
            <QboTypography style={{ fontSize: '2rem' }}>Request Query Parameters</QboTypography>
            <TooltipWrapper condition title="Copy to clipboard">
              <IconButton
                style={{ width: 'max-content' }}
                sx={{
                  position: 'relative',
                  right: '0.7rem',
                }}
                onClick={() => copyToClipboard(auditLog.request_body)}>
                <ContentCopy sx={{ width: '2rem', height: '2rem' }} />
              </IconButton>
            </TooltipWrapper>
          </div>
          <SyntaxHighlighter
            language="javascript"
            style={prism}
            showLineNumbers
            customStyle={{
              borderRadius: '5px',
              fontSize: '14px',
              padding: '10px',
            }}>
            {JSON.stringify(auditLog.request_body, null, 2)}
          </SyntaxHighlighter>
        </>
      )}
    </QboModal>
  );
}

AuditLogDetailsSection.propTypes = {
  auditLog: PropTypes.object,
  onClose: PropTypes.func,
};

AuditLogDetailsSection.defaultProps = {
  auditLog: null,
  onClose: () => {},
};
